import * as React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { CustomerFormSubmissions } from '../../store/slices';
import { AppDialog, EmptyState, Panel } from '../ui';
import { digitsOnly } from '../../lib/orderFilters';

interface Props {
    types: { id: number; label: string }[];
    loading: boolean;
}

/** The catalogue of form types (id + label) with create and delete. */
export default function FormSubmissionTypesTable({ types, loading }: Props) {
    const dispatch = useDispatch();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [newId, setNewId] = useState('');
    const [newLabel, setNewLabel] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
    const [pending, setPending] = useState(false);
    const formId = React.useId();

    const openCreate = () => {
        setNewId('');
        setNewLabel('');
        setDialogOpen(true);
    };

    const handleCreate = async (event?: React.FormEvent) => {
        event?.preventDefault();
        const id = parseInt(newId, 10);
        if (Number.isNaN(id) || pending) return;
        setPending(true);
        try {
            await dispatch<any>(CustomerFormSubmissions.createFormSubmissionTypeThunk({ id, label: newLabel || undefined }));
            setDialogOpen(false);
        } finally {
            setPending(false);
        }
    };

    const handleDelete = async () => {
        if (deleteConfirm === null || pending) return;
        setPending(true);
        try {
            await dispatch<any>(CustomerFormSubmissions.removeFormSubmissionType(deleteConfirm));
            setDeleteConfirm(null);
        } finally {
            setPending(false);
        }
    };

    return (
        <>
            <Panel
                title="Form submission types"
                subtitle="The id a form sends with its data and the label shown in this panel."
                padding="none"
                actions={<Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>Add type</Button>}
            >
                {types.length === 0 && !loading ? (
                    <EmptyState title="No form types yet" hint="Add the ids the shop forms send." action={<Button variant="outlined" onClick={openCreate}>Add type</Button>} />
                ) : (
                    <TableContainer>
                        <Table size="small" aria-label="Form submission types">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 120 }}>ID</TableCell>
                                    <TableCell>Label</TableCell>
                                    <TableCell align="right" sx={{ width: 80 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {types.map((type) => (
                                    <TableRow key={type.id} hover>
                                        <TableCell sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>#{type.id}</TableCell>
                                        <TableCell>{type.label || '—'}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Delete type">
                                                <IconButton size="small" color="error" onClick={() => setDeleteConfirm(type.id)} aria-label={`Delete type ${type.id}`}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Panel>

            <AppDialog
                open={dialogOpen}
                onClose={() => !pending && setDialogOpen(false)}
                title="New form type"
                description="The id must match the typeId the shop form sends."
                pending={pending}
                maxWidth="xs"
                actions={(
                    <>
                        <Button variant="text" onClick={() => setDialogOpen(false)} disabled={pending}>Cancel</Button>
                        <Button variant="contained" type="submit" form={formId} disabled={!newId || pending}>Create</Button>
                    </>
                )}
            >
                <form id={formId} onSubmit={handleCreate} style={{ display: 'grid', gap: 16, paddingTop: 4 }}>
                    <TextField label="ID" size="small" required value={newId} inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} onChange={(event) => setNewId(digitsOnly(event.target.value))} autoFocus />
                    <TextField label="Label" size="small" value={newLabel} onChange={(event) => setNewLabel(event.target.value)} />
                </form>
            </AppDialog>

            <AppDialog
                open={deleteConfirm !== null}
                onClose={() => !pending && setDeleteConfirm(null)}
                title={`Delete form type #${deleteConfirm ?? ''}`}
                description="Submissions of this type keep their typeId but lose the label."
                pending={pending}
                maxWidth="xs"
                actions={(
                    <>
                        <Button variant="text" onClick={() => setDeleteConfirm(null)} disabled={pending}>Cancel</Button>
                        <Button variant="contained" color="error" onClick={handleDelete} disabled={pending}>Delete</Button>
                    </>
                )}
            />
        </>
    );
}
